import { Column, Filter } from '@nirvana/shared/dtos/types/shared/filters';
import {
  ActionTakenEnum,
  Category,
  DetectingModules,
  DetectingTechnologies,
} from '@nirvana/shared/dtos/types/shared/threats';
import { CommonThreatsNavigationHelper } from './common.threats.navigation.helper';
import { AccessDataProtectionNavigationFiltersParams } from '../navigation.interfaces';

export class AccessDataProtectionNavigationHelper {
  public static getDisplayedColumns(): Array<Column> {
    return [Column.LAST_DETECTED, Column.DETECTING_MODULE, Column.DETECTING_TECHNOLOGY, Column.ACTION_TAKEN];
  }

  public static getFiltersForNavigationItem(params: AccessDataProtectionNavigationFiltersParams): Array<Filter> {
    const filters = [
      CommonThreatsNavigationHelper.getLastDetectedOnFilterFromTimeRange(params.timeRange),
      AccessDataProtectionNavigationHelper.getCategoryFilter(params.itemKey),
      AccessDataProtectionNavigationHelper.getActionsTakenFilter(params.itemKey),
      CommonThreatsNavigationHelper.getDeviceNameFilter(),
      AccessDataProtectionNavigationHelper.getDetectingModuleFilter(params.itemKey),
      AccessDataProtectionNavigationHelper.getDetectingTechnologyFilter(params.itemKey),
    ];

    return !params.isPartner ? filters : filters.concat(CommonThreatsNavigationHelper.getCompanyFilter(params.targets));
  }

  public static getActionsTakenValues(itemKey: string): Array<ActionTakenEnum> {
    let actionsTaken: Array<ActionTakenEnum>;

    switch (itemKey) {
      case 'title':
      case 'deviceControl':
        actionsTaken = [ActionTakenEnum.BLOCKED, ActionTakenEnum.READ_ONLY];
        break;
      case 'connectionsBlocked':
      case 'applications':
      case 'websitesBlocked':
        actionsTaken = [ActionTakenEnum.BLOCKED];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return actionsTaken;
  }

  public static getDetectingModuleValues(itemKey: string): Array<DetectingModules> {
    let detectingModules: Array<DetectingModules>;

    switch (itemKey) {
      case 'title':
        detectingModules = [
          DetectingModules.NETWORK_PROTECTION,
          DetectingModules.DEVICE_CONTROL,
          DetectingModules.FIREWALL,
        ];
        break;
      case 'deviceControl':
        detectingModules = [DetectingModules.DEVICE_CONTROL];
        break;
      case 'connectionsBlocked':
        detectingModules = [DetectingModules.FIREWALL];
        break;
      case 'applications':
        detectingModules = [DetectingModules.NETWORK_PROTECTION];
        break;
      case 'websitesBlocked':
        detectingModules = [DetectingModules.NETWORK_PROTECTION];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return detectingModules;
  }

  public static getDetectingTechnologyValues(itemKey: string): Array<DetectingTechnologies> {
    let detectingTechnologies: Array<DetectingTechnologies>;

    switch (itemKey) {
      case 'title':
        detectingTechnologies = [
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_CATEGORIES,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_SCHEDULER,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_EXCLUSIONS,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_DATA_PROTECTION,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_APPLICATION_BLACKLISTING,
          DetectingTechnologies.DEVICE_CONTROL_DEVICE_CONTROL,
          DetectingTechnologies.FIREWALL_PORT_SCAN,
          DetectingTechnologies.FIREWALL_PROCESS_NETWORK_TRAFFIC,
          DetectingTechnologies.FIREWALL_NETWORK_TRAFFIC,
        ];
        break;
      case 'deviceControl':
        detectingTechnologies = [DetectingTechnologies.DEVICE_CONTROL_DEVICE_CONTROL];
        break;
      case 'connectionsBlocked':
        detectingTechnologies = [
          DetectingTechnologies.FIREWALL_PORT_SCAN,
          DetectingTechnologies.FIREWALL_PROCESS_NETWORK_TRAFFIC,
          DetectingTechnologies.FIREWALL_NETWORK_TRAFFIC,
        ];
        break;
      case 'applications':
        detectingTechnologies = [DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_APPLICATION_BLACKLISTING];
        break;
      case 'websitesBlocked':
        detectingTechnologies = [
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_CATEGORIES,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_SCHEDULER,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_WEB_ACCESS_CONTROL_EXCLUSIONS,
          DetectingTechnologies.NETWORK_PROTECTION_CONTENT_CONTROL_DATA_PROTECTION,
        ];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }
    return detectingTechnologies;
  }

  private static getActionsTakenFilter(itemKey: string): Filter {
    return {
      actionTaken: {
        in: AccessDataProtectionNavigationHelper.getActionsTakenValues(itemKey),
      },
    };
  }

  private static getDetectingModuleFilter(itemKey: string): Filter {
    return {
      detectingModule: {
        in: AccessDataProtectionNavigationHelper.getDetectingModuleValues(itemKey),
      },
    };
  }

  private static getDetectingTechnologyFilter(itemKey: string): Filter {
    return {
      detectingTechnology: {
        in: AccessDataProtectionNavigationHelper.getDetectingTechnologyValues(itemKey),
      },
    };
  }

  private static getCategoryFilter(itemKey: string): Filter {
    let categories: Array<Category>;

    switch (itemKey) {
      case 'title':
        categories = [Category.WEBSITE, Category.CONNECTION, Category.DEVICE, Category.APPLICATION];
        break;
      case 'deviceControl':
        categories = [Category.DEVICE];
        break;
      case 'connectionsBlocked':
        categories = [Category.CONNECTION];
        break;
      case 'applications':
        categories = [Category.APPLICATION];
        break;
      case 'websitesBlocked':
        categories = [Category.WEBSITE];
        break;
      default:
        throw new Error(`Unknown category ${itemKey}.`);
    }

    return {
      category: {
        in: categories,
      },
    };
  }
}
